import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, GitBranch, GitPullRequest, Play, Plus, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FEATURE_GATE_STEPS, FEATURE_STEPS, type ConversationMode, type FeatureRunDTO, type RepoLinkDTO } from '@harness/shared';
import { GithubIcon, JiraIcon, Spinner, StatusDot, statusLabel } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { clockTime, relTime } from '../../lib/format';
import { qk } from '../../lib/keys';
import { useAudit, useConversation, useFeature, useFeatures, useIntegrations, useIssues, useMemory, type ProjectDetail } from '../../lib/queries';
import { useCan, VIEWER_HINT } from '../../lib/role';
import { toast, ui, useUI } from '../../lib/store';
import { runPrimaryAction } from './FeatureDialogs';
import TicketPanel from './TicketPanel';

export type CtxTab = 'tasks' | 'feature' | 'memory' | 'server' | 'spec';

/** Side-panel tabs per workspace mode (the first is the default). */
export const MODE_TABS: Record<ConversationMode, [CtxTab, string][]> = {
  feature: [
    ['tasks', 'Tasks'],
    ['feature', 'Feature'],
    ['memory', 'Memory'],
    ['server', 'Server'],
  ],
  ticket: [
    ['spec', 'Spec'],
    ['tasks', 'Board'],
    ['memory', 'Memory'],
  ],
  wireframe: [['memory', 'Memory']], // the Wireframes tab shows its preview panel instead
  analysis: [
    ['memory', 'Memory'],
    ['server', 'Server'],
  ],
};

const WAITING = new Set(['awaiting_transition', 'awaiting_plan', 'awaiting_push', 'awaiting_ticket']);

export default function ContextPanel({
  project,
  mode = 'feature',
  convId,
  tab,
  onTab,
  drawer,
}: {
  project: ProjectDetail;
  mode?: ConversationMode;
  convId?: string;
  tab: CtxTab;
  onTab: (t: CtxTab) => void;
  drawer: boolean;
}) {
  const conv = useConversation(convId);
  const features = useFeatures(project.id);
  const runId = conv.data?.conversation.featureRunId ?? features.data?.find((r) => !['done', 'cancelled'].includes(r.state))?.id ?? features.data?.[0]?.id;
  const run = useFeature(runId);
  const waiting = run.data ? WAITING.has(run.data.state) : false;
  const tabs = MODE_TABS[mode];
  return (
    <aside className={`card ws-card ctx ${drawer ? 'drawer' : ''}`} aria-label="Context">
      <div className="ctx-tabs" role="tablist">
        {tabs.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} className="ctx-tab" onClick={() => onTab(k)}>
            {l}
            {k === 'feature' && waiting && <span className="dot sm dot-accent" aria-label="Waiting for approval" />}
          </button>
        ))}
      </div>
      <div className="ctx-body" role="tabpanel">
        {tab === 'spec' && <TicketPanel project={project} convId={convId} />}
        {tab === 'tasks' && <Tasks project={project} runs={features.data ?? []} onView={() => onTab('feature')} readOnly={mode !== 'feature'} />}
        {tab === 'feature' && <Feature project={project} run={run.data} loading={run.isLoading && !!runId} onTasks={() => onTab('tasks')} />}
        {tab === 'memory' && <Memory project={project} />}
        {tab === 'server' && <ServerTab project={project} />}
      </div>
    </aside>
  );
}

function Tasks({ project, runs, onView, readOnly }: { project: ProjectDetail; runs: FeatureRunDTO[]; onView: () => void; readOnly?: boolean }) {
  const integ = useIntegrations(project.id);
  const jira = integ.data?.jira;
  const issues = useIssues(project.id, !!jira);
  const qc = useQueryClient();
  const can = useCan();
  const [refreshing, setRefreshing] = useState(false);
  if (integ.isLoading) return <div className="empty"><Spinner /></div>;
  if (!jira)
    return (
      <div className="empty">
        No Jira board linked. <Link to={`/p/${project.slug}/settings/integrations`}>Link GitHub and Jira</Link> to start features from tasks.
      </div>
    );
  const refresh = async () => {
    setRefreshing(true);
    try {
      qc.setQueryData(qk.issues(project.id), await api.get(`/api/projects/${project.id}/jira/issues?refresh=1`));
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setRefreshing(false);
    }
  };
  // A task whose latest feature run is done stays open in Jira (it's In Review), so hide it here.
  const latestRun = new Map<string, FeatureRunDTO>();
  for (const r of runs) {
    const cur = latestRun.get(r.issueKey);
    if (!cur || r.createdAt > cur.createdAt) latestRun.set(r.issueKey, r);
  }
  const open = (issues.data ?? []).filter((t) => latestRun.get(t.key)?.state !== 'done');
  const finished = (issues.data?.length ?? 0) - open.length;
  return (
    <>
      <div className="tile tile-blue" style={{ fontSize: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, font: '800 13px var(--font-heading)', marginBottom: 4 }}>
          <JiraIcon style={{ color: 'var(--tile-blue-ink)' }} />
          <span className="ellipsis" style={{ flex: 1 }}>
            {jira.projectKey} board · {jira.siteUrl.replace(/^https?:\/\//, '')}
          </span>
          <button className="btn btn-icon sm" title="Refresh" aria-label="Refresh tasks" onClick={refresh} style={{ color: 'var(--tile-blue-text)' }}>
            {refreshing ? <Spinner /> : <RefreshCw className="icon" />}
          </button>
        </div>
        <div className="mono" style={{ fontSize: 11, color: 'var(--color-neutral-800)' }}>
          {jira.jqlFilter}
        </div>
      </div>
      {issues.isLoading && <div className="empty"><Spinner /></div>}
      {issues.error && <div className="empty err">{errorText(issues.error)}</div>}
      {open.length === 0 && issues.data && <div className="empty">No open issues match the filter.</div>}
      {finished > 0 && (
        <div className="faint" style={{ fontSize: 12, padding: '0 2px' }}>
          {finished} task{finished === 1 ? '' : 's'} with a finished feature run hidden
        </div>
      )}
      {open.map((t) => {
        const active = runs.find((r) => r.issueKey === t.key && !['done', 'cancelled', 'failed'].includes(r.state));
        return (
          <div key={t.key} className="inner-card task">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
              <a className="mono" style={{ fontWeight: 600, color: 'var(--color-text)', textDecoration: 'none' }} href={t.url} target="_blank" rel="noreferrer">
                {t.key}
              </a>
              <span className="tag tag-neutral">{t.status}</span>
              <span style={{ marginLeft: 'auto', font: '800 12px var(--font-heading)', color: /high|highest|critical|blocker/i.test(t.priority) ? 'var(--color-accent-700)' : 'var(--color-neutral-700)' }}>{t.priority}</span>
            </div>
            <div className="task-title">{t.summary}</div>
            {readOnly ? null : active ? (
              <button className="btn btn-ghost accent" style={{ alignSelf: 'flex-start' }} onClick={onView}>
                Feature run in progress
                <ArrowRight className="icon" />
              </button>
            ) : (
              <button
                className="btn btn-secondary btn-sm"
                style={{ alignSelf: 'flex-start' }}
                disabled={!can.edit}
                title={can.edit ? undefined : VIEWER_HINT}
                onClick={() => ui.set({ repoPick: { projectId: project.id, issueKey: t.key } })}
              >
                <Play className="icon" />
                Start feature
              </button>
            )}
          </div>
        );
      })}
    </>
  );
}

function Feature({ project, run, loading, onTasks }: { project: ProjectDetail; run?: FeatureRunDTO; loading: boolean; onTasks: () => void }) {
  const [clicked, setBusy] = useState(false);
  const can = useCan();
  const runBusy = useUI((s) => !!(run && s.featureBusy[run.id]));
  const busy = clicked || runBusy;
  if (loading) return <div className="empty"><Spinner /></div>;
  if (!run)
    return (
      <div className="empty">
        No feature run yet. Pick a task in{' '}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onTasks();
          }}
        >
          Tasks
        </a>{' '}
        and choose Start feature.
      </div>
    );
  const done = run.state === 'done';
  const primary = primaryLabel(run);
  return (
    <>
      <div>
        <div className="micro">Feature run · {run.issueKey}</div>
        <div className="title-900" style={{ fontSize: 18, lineHeight: 1.25, marginTop: 2 }}>
          {run.issueSummary}
        </div>
        <Link to={`/p/${project.slug}/c/${run.conversationId}`} style={{ fontSize: 12 }}>
          Open conversation
        </Link>
      </div>
      <ol className="steps">
        {FEATURE_STEPS.map((label, i) => {
          const isDone = done || i < run.step;
          const cur = !done && i === run.step;
          return (
            <li key={label} className={cur ? 'cur' : ''}>
              <span className={`step-mark ${isDone ? 'done' : cur ? 'cur' : ''}`}>{isDone ? '✓' : i + 1}</span>
              <span>{label}</span>
              {FEATURE_GATE_STEPS.includes(i) && <span className="tag tag-gate sm">Gate</span>}
            </li>
          );
        })}
      </ol>
      <>
        {run.error && (
          <div className="note error" style={{ marginLeft: 0 }}>
            <span>
              <b>{run.error.text}</b>
              <div>{run.error.fix}</div>
            </span>
          </div>
        )}
        {run.repos.map((r) => (
          <div key={r.repoId} className="inner-card repo-card">
            <div className="name">
              <GithubIcon />
              <span className="ellipsis">{r.name}</span>
              <span className="tag tag-neutral" style={{ marginLeft: 'auto' }}>
                {r.role}
              </span>
            </div>
            <div className="mono" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-neutral-800)' }}>
              <GitBranch className="icon" />
              <span className="ellipsis">{r.branch}</span>
            </div>
            <div className="muted">{r.status}</div>
            {r.prUrl && (
              <a href={r.prUrl} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <GitPullRequest className="icon" />
                {r.name}#{r.prNumber}
              </a>
            )}
          </div>
        ))}
        {primary && (
          <button
            className="btn btn-primary btn-block"
            disabled={busy || !can.edit}
            title={can.edit ? undefined : VIEWER_HINT}
            onClick={async () => {
              setBusy(true);
              try {
                await runPrimaryAction(run, project);
              } catch (e) {
                toast(errorText(e), 'error');
              } finally {
                setBusy(false);
              }
            }}
          >
            {primary}
            {busy ? <Spinner /> : <ArrowRight className="icon" />}
          </button>
        )}
      </>
    </>
  );
}

function primaryLabel(run: FeatureRunDTO) {
  switch (run.state) {
    case 'awaiting_transition':
      return 'Approve: move to In progress';
    case 'awaiting_plan':
      return 'Approve plan';
    case 'awaiting_push':
      return 'Open push preview';
    case 'awaiting_ticket':
      return 'Open ticket preview';
    case 'failed':
      return 'Retry';
    default:
      return null;
  }
}

function Memory({ project }: { project: ProjectDetail }) {
  const qc = useQueryClient();
  const can = useCan();
  const { data, isLoading } = useMemory(project.id);
  const [text, setText] = useState('');
  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setText('');
    try {
      await api.post(`/api/projects/${project.id}/memory`, { text: t });
      await qc.invalidateQueries({ queryKey: qk.memory(project.id) });
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  const facts = data?.facts ?? [];
  const on = facts.filter((f) => f.enabled).length;
  return (
    <>
      <div className="tile tile-lavender" style={{ font: '700 12.5px var(--font-heading)' }}>
        {project.memoryEnabled ? `${on} of ${facts.length} facts on · top ${project.memoryTopK} injected per turn` : 'Memory is off for this project'}
        {(data?.proposals.length ?? 0) > 0 && (
          <div style={{ marginTop: 6 }}>
            <Link to={`/p/${project.slug}/settings/memory`} style={{ fontWeight: 600 }}>
              {data!.proposals.length} proposed fact{data!.proposals.length === 1 ? '' : 's'} to review →
            </Link>
          </div>
        )}
      </div>
      {isLoading && <div className="empty"><Spinner /></div>}
      {facts.map((f) => (
        <div key={f.id} className="inner-card" style={{ padding: '10px 12px', opacity: f.enabled ? 1 : 0.5 }}>
          <div style={{ fontSize: 13, textWrap: 'pretty' }}>{f.text}</div>
          <div className="faint" style={{ fontSize: 11.5, marginTop: 3 }}>
            {f.source === 'agent' ? 'Saved by an agent' : f.source === 'auto' ? 'Approved from a conversation' : 'Added by you'}
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6 }}>
        <input className="input" placeholder="Add a fact the agents should know" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void add()} aria-label="New fact" />
        <button className="btn btn-icon btn-secondary" onClick={add} title={can.edit ? 'Add' : VIEWER_HINT} disabled={!can.edit} style={{ flex: 'none' }}>
          <Plus className="icon" style={{ fontSize: 15 }} />
        </button>
      </div>
      <Link to={`/p/${project.slug}/settings/memory`} style={{ fontSize: 13 }}>
        Manage memory
      </Link>
    </>
  );
}

function ServerTab({ project }: { project: ProjectDetail }) {
  const s = project.server;
  const integ = useIntegrations(project.id);
  const audit = useAudit(project.id, 4);
  const qc = useQueryClient();
  const [syncing, setSyncing] = useState<Record<string, boolean>>({});
  const sync = async (r: RepoLinkDTO) => {
    setSyncing((x) => ({ ...x, [r.id]: true }));
    try {
      await api.post(`/api/projects/${project.id}/repos/${r.id}/sync`);
      await qc.invalidateQueries({ queryKey: qk.integrations(project.id) });
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setSyncing((x) => ({ ...x, [r.id]: false }));
    }
  };
  if (!s)
    return (
      <div className="empty">
        No server connected. <Link to={`/p/${project.slug}/settings/server`}>Connect one</Link> so agents can read, edit and run code where it lives.
      </div>
    );
  const mode = s.mode === 'runner' ? `Harness Runner${s.runnerVersion ? ` ${s.runnerVersion}` : ''}` : s.mode === 'ssh' ? 'SSH' : s.mode === 'daytona' ? 'Daytona' : 'Local host';
  return (
    <>
      <div className="stat-grid">
        <div className={`tile ${s.status === 'online' ? 'tile-mint' : s.status === 'unreachable' ? 'tile-cream' : 'tile-neutral'}`}>
          <div className="tile-label">Status</div>
          <div className="v">
            <StatusDot status={s.status} />
            {statusLabel(s.status)}
          </div>
        </div>
        <div className="tile tile-lavender">
          <div className="tile-label" style={{ color: 'var(--tile-lavender-ink)' }}>
            Mode
          </div>
          <div className="v ellipsis" title={mode} style={{ display: 'block' }}>
            {s.mode === 'runner' ? 'Runner' : s.mode === 'ssh' ? 'SSH' : s.mode === 'daytona' ? 'Daytona' : 'Local host'}
          </div>
        </div>
        <div className="tile tile-neutral">
          <div className="tile-label faint">Host</div>
          <div className="v mono" title={s.host}>
            {s.host || '—'}
          </div>
        </div>
        <div className="tile tile-neutral" style={{ background: '#fff' }}>
          <div className="tile-label faint">Working dir</div>
          <div className="v mono" title={s.workingDir}>
            {s.workingDir}
          </div>
        </div>
      </div>
      <div className="micro ctx-label">Linked repositories</div>
      {(integ.data?.repos ?? []).length === 0 && (
        <div className="faint" style={{ fontSize: 13 }}>
          None linked. <Link to={`/p/${project.slug}/settings/integrations`}>Link repos</Link>
        </div>
      )}
      {integ.data?.repos.map((r) => (
        <div key={r.id} className="inner-card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', fontSize: 13 }}>
          <GithubIcon />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="ellipsis" style={{ display: 'block', font: '800 13.5px var(--font-heading)' }}>
              {r.owner}/{r.repo}
            </span>
            <span className="mono muted ellipsis" style={{ display: 'block', fontSize: 11 }}>
              {r.localPath} · {r.baseBranch} · {r.syncStatus === 'syncing' || syncing[r.id] ? 'syncing…' : r.syncStatus === 'error' ? 'sync failed' : r.lastSyncAt ? `synced ${relTime(r.lastSyncAt).toLowerCase()}` : 'not synced'}
            </span>
          </span>
          <button className="btn btn-icon btn-secondary sm" title="Sync" onClick={() => sync(r)} disabled={syncing[r.id]}>
            {syncing[r.id] ? <Spinner /> : <RefreshCw className="icon" style={{ fontSize: 14 }} />}
          </button>
        </div>
      ))}
      <div className="micro ctx-label">Recent activity</div>
      <div className="inner-card audit-list">
      {audit.data?.map((a) => (
        <div key={a.id} className="audit-row">
          <span className="mono faint">{clockTime(a.createdAt).slice(0, 8)}</span>
          <span className="ellipsis" title={a.detail}>
            <b>{a.actor}</b> {a.detail}
          </span>
        </div>
      ))}
      {!audit.data?.length && <div className="audit-row faint">No activity yet</div>}
      </div>
      <Link to={`/p/${project.slug}/settings/server`} style={{ fontSize: 13 }}>
        Full audit log and checkpoints
      </Link>
    </>
  );
}
